import React, { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavContainer, NavItem, NavMain } from './styled';
import type { NavBarProps } from '../types';
import { Language } from '../../../utils/types';

const FORM_LINK: Record<Language, string> = {
  [Language.az]:
    'https://www.pasharealestate.az/az/elaqe?utm_source=binaazpre&utm_medium=referral',
  [Language.ru]:
    'https://www.pasharealestate.az/ru/svyazatsya?utm_source=binaazpre&utm_medium=referral'
};

export const NavBar: React.FC<NavBarProps> = ({
  projectsRef,
  locationRef,
  aboutRef,
  advantagesRef,
  visibleTabRef,
  pashaItemsRef,
  fallbackMapRef
}) => {
  const { t, i18n } = useTranslation();
  const [lockedRef, setLockedRef] =
    useState<RefObject<HTMLDivElement | null> | null>(null);

  const getFormLink = () => {
    const raw = i18n.language?.toLowerCase() ?? Language.az;

    const lang: Language = raw.startsWith(Language.ru)
      ? Language.ru
      : Language.az;

    return FORM_LINK[lang];
  };

  const STABILITY_MS = 200;
  const THRESHOLD = 0.6;

  const handleClick = (
    ref: RefObject<HTMLDivElement | null> | null,
    fallbackRef?: RefObject<HTMLDivElement | null> | null
  ) => {
    const targetRef = ref ?? fallbackRef;
    const target = targetRef?.current;
    if (!target) return;

    setLockedRef(targetRef);

    target.scrollIntoView({ behavior: 'smooth', block: 'start' });

    let timer: number | null = null;
    const io = new IntersectionObserver(
      entries => {
        const inView = entries.some(e => e.isIntersecting);
        if (inView) {
          if (timer == null)
            timer = window.setTimeout(() => {
              setLockedRef(null);
            }, STABILITY_MS);
        } else if (timer) {
          window.clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: THRESHOLD }
    );

    io.observe(target);
    window.setTimeout(() => {
      io.disconnect();
      if (timer) {
        window.clearTimeout(timer);
        timer = null;
      }
    }, 1000);
  };

  const isActive = (ref: RefObject<HTMLDivElement | null> | null) =>
    lockedRef
      ? ref?.current === lockedRef.current
      : !!(
          ref?.current &&
          visibleTabRef?.current &&
          ref.current === visibleTabRef.current
        );

  return (
    <NavContainer role="navigation" data-cy="pasha-navbar">
      <NavMain>
        <NavItem
          onClick={() => handleClick(projectsRef)}
          active={isActive(projectsRef)}
          data-cy="pasha-navbar-projects"
          data-stat="nav_projects"
        >
          {t('pasha.top_bar_projects')}
        </NavItem>
        <NavItem
          onClick={() => handleClick(locationRef, fallbackMapRef)}
          active={isActive(locationRef)}
          data-cy="pasha-navbar-locations"
          data-stat="nav_locations"
        >
          {t('pasha.top_bar_locations')}
        </NavItem>
        <NavItem
          onClick={() => handleClick(pashaItemsRef)}
          active={isActive(pashaItemsRef)}
          data-cy="pasha-navbar-advise"
          data-stat="nav_offers"
        >
          {t('pasha.top_bar_advise')}
        </NavItem>
        <NavItem
          onClick={() => handleClick(advantagesRef)}
          active={isActive(advantagesRef)}
          data-cy="pasha-navbar-advantage"
          data-stat="nav_advantages"
        >
          {t('pasha.top_bar_advantage')}
        </NavItem>

        <NavItem
          onClick={() => handleClick(aboutRef)}
          active={isActive(aboutRef)}
          data-cy="pasha-navbar-about"
          data-stat="nav_about"
        >
          {t('pasha.top_bar_about')}
        </NavItem>
      </NavMain>

      <NavItem
        as="a"
        href={getFormLink()}
        target="_blank"
        rel="noopener noreferrer"
        highlight
        data-cy="pasha-navbar-form"
        data-stat="nav_apply"
      >
        {t('pasha.top_bar_form')}
      </NavItem>
    </NavContainer>
  );
};
